import { startCase } from "lodash";
import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { getPresignedUrl } from "../../../core/data/redux/awsBucketSlice";
import Api from "../../../core/data/redux/utils/apiCall";
import { BASE_URLS, API_URLS } from "../../../core/data/redux/apis/urls";
import {
  clearError,
  getNocOnlineCourse,
  setCurrentRequest,
} from "../../../core/data/redux/nocOnlineCoursesSlice";
import { getMyProfile } from "../../../core/data/redux/employeesSlice";
import { AppDispatch, RootState } from "../../../core/data/redux/store";
import { formatEmpIdForDisplay } from "../../../utils/empIdUtils";
import { all_routes } from "../../router/all_routes";
import {
  getRequestStatusBadgeClass as getGlobalStatusBadgeClass,
  getRequestStatusLabel as getGlobalStatusLabel,
} from "../../../core/data/constants/requestStatus";
import NOCChatComponent from "../../../components/NOCChat";

interface OnlineCourseDetailsProps { }

const OnlineCourseDetails: React.FC<OnlineCourseDetailsProps> = () => {
  const { id } = useParams();
  const dispatch = useDispatch<AppDispatch>();
  const routes = all_routes;

  // Redux state
  const { currentCourse, loading, error } = useSelector(
    (state: RootState) => state.nocOnlineCoursesState
  );
  const employeesState = useSelector((state: RootState) => state.employeesState);

  // Helper functions
  const formatDate = (dateString: string | Date | undefined | null): string => {
    if (!dateString || dateString === "Invalid date") return "-";
    try {
      const date =
        dateString instanceof Date ? dateString : new Date(dateString);
      if (isNaN(date.getTime())) return "-";
      const day = date.getDate().toString().padStart(2, "0");
      const month = (date.getMonth() + 1).toString().padStart(2, "0");
      const year = date.getFullYear();
      return `${day}-${month}-${year}`;
    } catch (error) {
      return "-";
    }
  };

  const formatYesNo = (value: string | undefined) => {
    if (!value) return "-";
    const lowerValue = value.toLowerCase();
    if (lowerValue === "y" || lowerValue === "yes") return "Yes";
    if (lowerValue === "n" || lowerValue === "no") return "No";
    return value;
  };

  const [isDownloading, setIsDownloading] = React.useState<boolean>(false);

  const handleDownloadPDF = async () => {
    if (!currentCourse || isDownloading) return;
    try {
      setIsDownloading(true);
      const payload = {
        empId: currentCourse.empId || (currentCourse as any).empID,
        type: "ZNOC",
        referenceNumber: currentCourse.referenceNumber,
      };

      const res = await Api.post(
        API_URLS.NOC.ONLINE_COURSE_DOWNLOAD,
        payload
      );

      const docItem = res?.data?.data?.[0] || res?.data;
      if (res?.data?.success && docItem?.url) {
        window.open(docItem.url, "_blank");
      } else {
        toast.error(res?.data?.message || "Failed to download NOC document.");
      }
    } catch (err: any) {
      console.error("Online Course NOC Download Error:", err);
      toast.error(err?.message || "Failed to download NOC document.");
    } finally {
      setIsDownloading(false);
    }
  };

  // Fetch data
  useEffect(() => {
    if (id) {
      dispatch(getNocOnlineCourse({ id: parseInt(id) }));
      dispatch(setCurrentRequest(parseInt(id)));
      dispatch(getMyProfile({}));
    }
  }, [id, dispatch]);

  // Handle errors
  useEffect(() => {
    if (error) {
      toast.error(error);
      dispatch(clearError());
    }
  }, [error, dispatch]);

  const handleDocumentClick = async (docParam: any) => {
    if (!docParam) {
      alert("Document file information is missing.");
      return;
    }

    // Check if docParam has embedded base64 content
    if (
      typeof docParam === "object" &&
      docParam.content &&
      typeof docParam.content === "string" &&
      docParam.content.trim() !== ""
    ) {
      const contentStr = docParam.content.trim();
      const base64Url = contentStr.startsWith("data:")
        ? contentStr
        : `data:application/pdf;base64,${contentStr}`;
      window.open(base64Url, "_blank");
      return;
    }

    const fileName =
      typeof docParam === "string"
        ? docParam
        : docParam.fileName || docParam.awsKey || docParam.UUID || docParam.docID;

    if (!fileName || fileName.trim() === "") {
      alert("Document key is missing. Unable to open file.");
      return;
    }

    if (fileName.startsWith("http://") || fileName.startsWith("https://")) {
      window.open(fileName, "_blank");
      return;
    }

    // Synchronously open a new tab to bypass browser popup blockers
    const popup = window.open("", "_blank");
    if (popup) {
      popup.document.title = "Loading document...";
      popup.document.body.innerHTML = `
        <div style="display:flex;justify-content:center;align-items:center;height:100vh;font-family:sans-serif;color:#555;">
          <div style="text-align:center;">
            <div style="font-size:18px;font-weight:600;margin-bottom:8px;">Loading document...</div>
            <div style="font-size:14px;color:#888;">Please wait while your file is prepared.</div>
          </div>
        </div>
      `;
    }

    try {
      const candidateKeys: string[] = [];
      if (fileName.includes("/")) {
        candidateKeys.push(fileName);
        const fileBasename = fileName.split("/").pop() || "";
        if (fileBasename) {
          candidateKeys.push(`noc_doc/${fileBasename}`);
          candidateKeys.push(fileBasename);
        }
      } else {
        candidateKeys.push(`noc_doc/${fileName}`);
        candidateKeys.push(fileName);
        candidateKeys.push(`NOC_ONLINECOURSE/${fileName}`);
        candidateKeys.push(`NOC_OUTJOB/${fileName}`);
      }

      // Request Presigned URL from S3 bucket endpoint
      for (const key of candidateKeys) {
        try {
          const endPoint = `${BASE_URLS.BASE_URL}aws-bucket/get-presigned-url`;
          const res: any = await Api.post(endPoint, { key });
          const resData = res?.data;
          let pUrl = "";
          if (typeof resData === "string") {
            pUrl = resData;
          } else if (typeof resData?.data === "string") {
            pUrl = resData.data;
          } else if (typeof resData?.data?.url === "string") {
            pUrl = resData.data.url;
          } else if (typeof resData?.url === "string") {
            pUrl = resData.url;
          } else if (typeof resData?.presignedUrl === "string") {
            pUrl = resData.presignedUrl;
          }

          if (pUrl && (pUrl.startsWith("http://") || pUrl.startsWith("https://"))) {
            if (popup && !popup.closed) {
              popup.location.href = pUrl;
            } else {
              window.open(pUrl, "_blank");
            }
            return;
          }
        } catch (keyErr) {
          console.warn(`Failed trying S3 key "${key}":`, keyErr);
        }
      }

      // Fallback: try docID endpoint if presigned URL is unavailable
      const docID =
        typeof docParam === "object"
          ? docParam.docID || docParam.docId || docParam.id
          : null;
      if (docID) {
        try {
          const docRes: any = await Api.post(
            BASE_URLS.BASE_URL + "noc/noc-outjob-doc-by-docid",
            { docId: docID }
          );
          const docData =
            docRes?.data?.d?.results?.[0] ||
            docRes?.data?.data?.d?.results?.[0] ||
            docRes?.data?.data;
          const base64Content =
            docData?.Content || docData?.FileContent || docData?.content;
          if (base64Content) {
            const cleanBase64 = base64Content.replace(/^data:.*?,/, "");
            const byteCharacters = atob(cleanBase64);
            const byteNumbers = new Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i++) {
              byteNumbers[i] = byteCharacters.charCodeAt(i);
            }
            const byteArray = new Uint8Array(byteNumbers);
            const pdfBlob = new Blob([byteArray], { type: "application/pdf" });
            const blobUrl = URL.createObjectURL(pdfBlob);
            if (popup && !popup.closed) {
              popup.location.href = blobUrl;
            } else {
              window.open(blobUrl, "_blank");
            }
            return;
          }
        } catch (apiErr) {
          console.error("Error fetching doc by docID:", apiErr);
        }
      }

      if (popup && !popup.closed) {
        popup.close();
      }
      alert("Unable to open file. Please try again.");
    } catch (error) {
      if (popup && !popup.closed) {
        popup.close();
      }
      console.error("Error opening document:", error);
      alert("Unable to open file. Please try again.");
    }
  };

  if (loading && !currentCourse) {
    return (
      <div className="page-wrapper">
        <div className="content">
          <div
            className="d-flex justify-content-center align-items-center"
            style={{ height: "50vh" }}
          >
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!currentCourse) {
    return (
      <div className="page-wrapper">
        <div className="content">
          <div className="alert alert-warning">
            <i className="ti ti-alert-circle me-2"></i>
            Online course details not found. Please go back and try again.
          </div>
          <Link to={routes.noc.onlineCourse} className="btn btn-primary">
            <i className="ti ti-arrow-left me-2"></i>
            Back to List
          </Link>
        </div>
      </div>
    );
  }

  // Employee details resolution with full fallbacks
  const profileUser = (employeesState as any)?.data?.data;
  const profileDetails = profileUser?.details || profileUser || {};

  let localUser: any = {};
  try {
    const userStr = localStorage.getItem("user");
    if (userStr && userStr !== "undefined") {
      localUser = JSON.parse(userStr);
    }
  } catch (e) { }
  const localDetails = localUser?.details || localUser || {};

  const appAny = (currentCourse || {}) as any;
  const empObj =
    appAny.employee ||
    appAny.employeeDetails ||
    appAny.applicantDetails ||
    appAny.applicant ||
    appAny.emp_details ||
    appAny.userDetails ||
    profileUser ||
    localUser ||
    {};

  const empDetails = empObj.details || empObj || {};
  const fallbackObj =
    empDetails.firstname || empDetails.fullName || empDetails.empId
      ? empDetails
      : profileDetails.firstname || profileDetails.fullName || profileDetails.empId
        ? profileDetails
        : localDetails;

  const sapInfo =
    fallbackObj.sap_department_designation_wings ||
    empDetails.sap_department_designation_wings ||
    profileDetails.sap_department_designation_wings ||
    localDetails.sap_department_designation_wings ||
    {};

  const rawEmployeeName =
    (fallbackObj.firstname
      ? `${fallbackObj.firstname} ${fallbackObj.secondname || ""} ${fallbackObj.lsastname || fallbackObj.lastname || ""}`
        .replace(/\s+/g, " ")
        .trim()
      : "") ||
    fallbackObj.fullName ||
    fallbackObj.name ||
    appAny.employeeName ||
    appAny.empName ||
    "";
  const employeeName = rawEmployeeName || "-";

  const rawDesignation =
    sapInfo.designation_name ||
    fallbackObj.designation ||
    fallbackObj.designation_details?.longText ||
    fallbackObj.designation_details?.designationName ||
    (fallbackObj.designationID ? `Designation ID: ${fallbackObj.designationID}` : "") ||
    appAny.designation ||
    appAny.employee?.designation;
  const designation = rawDesignation || "-";

  const rawDoj =
    fallbackObj.date_of_joining ||
    empObj.date_of_joining ||
    appAny.date_of_joining ||
    appAny.dateOfJoining ||
    fallbackObj.doj;

  const rawEmpStatus =
    fallbackObj.employmentType ||
    empObj.employmentType ||
    empDetails.employmentType ||
    appAny.employee?.employmentType ||
    appAny.employee?.details?.employmentType ||
    appAny.employmentType ||
    fallbackObj.empStatus ||
    appAny.employmentStatus;

  const rawDepartment =
    appAny.department ||
    sapInfo.department_name ||
    fallbackObj.department_name ||
    fallbackObj.department_details?.departmentName ||
    (fallbackObj.department ? `Department ID: ${fallbackObj.department}` : "") ||
    "-";

  const rawContactNumber =
    appAny.contactNumber ||
    appAny.mobileNumber ||
    fallbackObj.phone ||
    fallbackObj.mobileNumber ||
    empObj.phone ||
    "-";

  const rawEmail =
    appAny.email ||
    appAny.emailId ||
    fallbackObj.email ||
    empObj.email ||
    "-";

  const approverName =
    appAny.approverName ||
    appAny.approver?.name ||
    "";
  const approverId =
    appAny.approverUserID ||
    appAny.approverId ||
    appAny.approver?.empId ||
    "";
  const approverDisplay =
    approverName && approverId
      ? `${approverName} (${formatEmpIdForDisplay(approverId)})`
      : approverName
        ? approverName
        : approverId
          ? formatEmpIdForDisplay(approverId)
          : "-";

  const requestStatusStr = currentCourse.requestStatus || (currentCourse as any).status || "T";

  return (
    <div className="page-wrapper">
      <div className="content">
        {/* Page Header */}
        <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
          <div className="my-auto mb-2">
            <div className="d-flex align-items-center">
              <Link
                to={routes.noc.onlineCourse}
                className="text-primary text-decoration-none me-3"
              >
                <i className="ti ti-arrow-left fs-24"></i>
              </Link>
              <h3 className="page-title mb-0">
                NOC for Online Course - {currentCourse.referenceNumber}
              </h3>
            </div>
            <nav className="mt-2">
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to={routes.employeeDashboard}>Dashboard</Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to={routes.noc.main}>NOC Services</Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to={routes.noc.onlineCourse}>Online Course</Link>
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  Course Details
                </li>
              </ol>
            </nav>
          </div>
          <div className="d-flex align-items-center gap-2 mt-2 mt-md-0">
            {requestStatusStr === "A" && (
              <Link
                to={`${routes.noc.onlineCourse}/create-reimbursement/${id}`}
                className="btn btn-primary d-flex align-items-center gap-2"
              >
                <i className="ti ti-receipt fs-16"></i>
                <span>Apply for Reimbursement / Addition</span>
              </Link>
            )}
            <button
              type="button"
              className="btn btn-outline-secondary d-flex align-items-center gap-2"
              onClick={handleDownloadPDF}
              disabled={isDownloading}
            >
              <i
                className={`ti ti-download fs-16 ${
                  isDownloading ? "fa-spin" : ""
                }`}
              ></i>
              <span>{isDownloading ? "Downloading..." : "Download NOC"}</span>
            </button>
          </div>
        </div>

        {/* Chat Component */}
        {currentCourse.id && (
          <NOCChatComponent
            nocRequestId={currentCourse.id}
            nocType="NOC_ONLINECOURSE"
            employeeName={employeeName}
            employeeId={currentCourse.empId || currentCourse.empID}
            employeeImage={empDetails?.image}
            approverId={
              currentCourse.approverUserID ||
              (currentCourse as any).approverId ||
              (currentCourse as any).reportingManagerID
            }
          />
        )}

        {/* 1. Header / Application Details Card */}
        <div className="card mt-4">
          <div className="card-header d-flex justify-content-between align-items-center">
            <div>
              <h4 className="mb-0">Application Details</h4>
              <p className="text-muted mb-0 mt-1">
                Reference Number: {currentCourse.referenceNumber || "-"}
              </p>
            </div>
            <div className="d-flex align-items-center gap-2">
              <span className="badge bg-primary fs-14">NOC Request</span>
              <span
                className={`badge ${getGlobalStatusBadgeClass(requestStatusStr)} fs-14`}
              >
                {getGlobalStatusLabel(requestStatusStr)}
              </span>
            </div>
          </div>
          <div className="card-body">
            <div className="row g-3">
              <div className="col-md-6 col-lg-3">
                <div className="p-3 bg-light rounded border h-100">
                  <small className="text-muted d-block uppercase fw-medium mb-1">Ref No.</small>
                  <div className="fw-bold text-dark fs-15">
                    {currentCourse.referenceNumber || "-"}
                  </div>
                </div>
              </div>
              <div className="col-md-6 col-lg-3">
                <div className="p-3 bg-light rounded border h-100">
                  <small className="text-muted d-block uppercase fw-medium mb-1">Employee ID</small>
                  <div className="fw-bold text-primary fs-15">
                    {formatEmpIdForDisplay(currentCourse.empId || currentCourse.empID || "")}
                  </div>
                </div>
              </div>
              <div className="col-md-6 col-lg-3">
                <div className="p-3 bg-light rounded border h-100">
                  <small className="text-muted d-block uppercase fw-medium mb-1">Currently With (Approver)</small>
                  <div className="fw-bold text-dark fs-15">
                    {approverDisplay}
                  </div>
                </div>
              </div>
              <div className="col-md-6 col-lg-3">
                <div className="p-3 bg-light rounded border h-100">
                  <small className="text-muted d-block uppercase fw-medium mb-1">Creation Date</small>
                  <div className="fw-bold text-dark fs-15">
                    {formatDate(currentCourse.requestCreationDate || (currentCourse as any).created_at)}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 2. Employee Details Card */}
        <div className="card mt-4">
          <div className="card-header">
            <h5 className="mb-0">
              <i className="ti ti-user me-2 text-primary"></i>
              Employee Details
            </h5>
          </div>
          <div className="card-body">
            <div className="row g-3">
              <div className="col-md-6 col-lg-3">
                <div className="p-3 bg-light rounded border h-100">
                  <small className="text-muted d-block uppercase fw-medium mb-1">Name</small>
                  <div className="fw-bold text-dark fs-15">
                    {employeeName}
                  </div>
                </div>
              </div>
              <div className="col-md-6 col-lg-3">
                <div className="p-3 bg-light rounded border h-100">
                  <small className="text-muted d-block uppercase fw-medium mb-1">Designation</small>
                  <div className="fw-bold text-dark fs-15">
                    {designation}
                  </div>
                </div>
              </div>
              <div className="col-md-6 col-lg-3">
                <div className="p-3 bg-light rounded border h-100">
                  <small className="text-muted d-block uppercase fw-medium mb-1">Date of Joining in DMRC</small>
                  <div className="fw-bold text-dark fs-15">
                    {formatDate(rawDoj)}
                  </div>
                </div>
              </div>
              <div className="col-md-6 col-lg-3">
                <div className="p-3 bg-light rounded border h-100">
                  <small className="text-muted d-block uppercase fw-medium mb-1">Employment Status</small>
                  <div className="fw-bold text-dark fs-15">
                    {rawEmpStatus || "-"}
                  </div>
                </div>
              </div>
              <div className="col-md-6 col-lg-4">
                <div className="p-3 bg-light rounded border h-100">
                  <small className="text-muted d-block uppercase fw-medium mb-1">Department</small>
                  <div className="fw-bold text-dark fs-15">
                    {rawDepartment}
                  </div>
                </div>
              </div>
              <div className="col-md-6 col-lg-4">
                <div className="p-3 bg-light rounded border h-100">
                  <small className="text-muted d-block uppercase fw-medium mb-1">Contact Number</small>
                  <div className="fw-bold text-dark fs-15">
                    {rawContactNumber}
                  </div>
                </div>
              </div>
              <div className="col-md-6 col-lg-4">
                <div className="p-3 bg-light rounded border h-100">
                  <small className="text-muted d-block uppercase fw-medium mb-1">Email-ID</small>
                  <div className="fw-bold text-dark fs-15">
                    {rawEmail}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 3. Application / Course Details Card */}
        <div className="card mt-4">
          <div className="card-header">
            <h5 className="mb-0">
              <i className="ti ti-briefcase me-2 text-primary"></i>
              Application Details
            </h5>
          </div>
          <div className="card-body">
            <div className="row g-3">
              <div className="col-md-6 col-lg-4">
                <div className="p-3 bg-light rounded border h-100">
                  <small className="text-muted d-block uppercase fw-medium mb-1">Nature of Duty Performing</small>
                  <div className="fw-bold text-dark fs-15">
                    {(() => {
                      if ((currentCourse as any)?.nature_of_duty?.nature_of_duty_title) {
                        return (currentCourse as any).nature_of_duty.nature_of_duty_title;
                      }
                      const val =
                        (currentCourse as any)?.natureOfDutyPerfo ||
                        (currentCourse as any)?.natureOfDuty ||
                        (currentCourse as any)?.NatureOfDutyPerfo ||
                        (currentCourse as any)?.NatureOfDuty ||
                        (typeof (currentCourse as any)?.nature_of_duty === "string"
                          ? (currentCourse as any).nature_of_duty
                          : "");
                      if (!val) return "-";
                      const map: Record<string, string> = {
                        G: "General",
                        S: "Shift",
                        g: "General",
                        s: "Shift",
                        R: "Regular",
                        r: "Regular",
                        "01": "Regular",
                        "02": "Shift",
                        "1": "Regular",
                        "2": "Shift",
                        general: "General",
                        shift: "Shift",
                        regular: "Regular",
                        General: "General",
                        Shift: "Shift",
                        Regular: "Regular",
                      };
                      return map[String(val).trim()] || String(val).trim();
                    })()}
                  </div>
                </div>
              </div>
              <div className="col-md-6 col-lg-4">
                <div className="p-3 bg-light rounded border h-100">
                  <small className="text-muted d-block uppercase fw-medium mb-1">Category of online Course</small>
                  <div className="fw-bold text-dark fs-15">
                    {currentCourse.onlineCourseCategory === "A1"
                      ? "General Course"
                      : currentCourse.onlineCourseCategory === "A2"
                        ? "Specialized Course"
                        : currentCourse.course_category?.category_title ||
                        currentCourse.onlineCourseCategory ||
                        "-"}
                  </div>
                </div>
              </div>
              <div className="col-md-6 col-lg-4">
                <div className="p-3 bg-light rounded border h-100">
                  <small className="text-muted d-block uppercase fw-medium mb-1">Course Department</small>
                  <div className="fw-bold text-dark fs-15">
                    {currentCourse.onlineCourseDepartment || "-"}
                  </div>
                </div>
              </div>
              <div className="col-md-12">
                <div className="p-3 bg-light rounded border h-100">
                  <small className="text-muted d-block uppercase fw-medium mb-1">Course Name</small>
                  <div className="fw-bold text-primary fs-15">
                    {currentCourse.onlineCourseName || "-"}
                  </div>
                </div>
              </div>
              <div className="col-md-12">
                <div className="p-3 bg-light rounded border h-100">
                  <small className="text-muted d-block uppercase fw-medium mb-1">Web URL</small>
                  <div className="fw-bold text-dark fs-15">
                    {currentCourse.courseBrochure ? (
                      currentCourse.courseBrochure.startsWith("http://") ||
                        currentCourse.courseBrochure.startsWith("https://") ? (
                        <a
                          href={currentCourse.courseBrochure}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary text-decoration-underline text-break"
                        >
                          {currentCourse.courseBrochure}
                        </a>
                      ) : (
                        <span
                          className="text-primary cursor-pointer text-decoration-underline text-break"
                          onClick={() => handleDocumentClick(currentCourse.courseBrochure)}
                        >
                          {currentCourse.courseBrochure}
                        </span>
                      )
                    ) : (
                      "-"
                    )}
                  </div>
                </div>
              </div>
              <div className="col-md-6 col-lg-4">
                <div className="p-3 bg-light rounded border h-100">
                  <small className="text-muted d-block uppercase fw-medium mb-1">Updated Course Fee in INR</small>
                  <div className="fw-bold text-dark fs-15">
                    {currentCourse.courseFee
                      ? `₹ ${Number(currentCourse.courseFee).toLocaleString("en-IN")}`
                      : "-"}
                  </div>
                </div>
              </div>
              <div className="col-md-6 col-lg-4">
                <div className="p-3 bg-light rounded border h-100">
                  <small className="text-muted d-block uppercase fw-medium mb-1">Last Date of Registration of Course</small>
                  <div className="fw-bold text-dark fs-15">
                    {formatDate(currentCourse.lastDateOfRegistration)}
                  </div>
                </div>
              </div>
              <div className="col-md-6 col-lg-4">
                <div className="p-3 bg-light rounded border h-100">
                  <small className="text-muted d-block uppercase fw-medium mb-1">Probable Date of Commencement of Course</small>
                  <div className="fw-bold text-dark fs-15">
                    {formatDate(currentCourse.dateOfCourseJoining)}
                  </div>
                </div>
              </div>
              <div className="col-md-12">
                <div className="p-3 bg-light rounded border h-100">
                  <small className="text-muted d-block uppercase fw-medium mb-1">Remarks</small>
                  <div className="fw-bold text-dark fs-15">
                    {(currentCourse as any).remarks || (currentCourse as any).Remarks || "-"}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 4. Course Details as per HR Compendium */}
        <div className="card mt-4">
          <div className="card-header">
            <h5 className="mb-0">
              <i className="ti ti-file-certificate me-2 text-primary"></i>
              Course details as per HR Compendium
            </h5>
          </div>
          <div className="card-body">
            <div className="row g-3">
              <div className="col-md-6 col-lg-4">
                <div className="p-3 bg-light rounded border h-100">
                  <small className="text-muted d-block uppercase fw-medium mb-1">Category</small>
                  <div className="fw-bold text-dark fs-15">
                    {currentCourse.course_category?.category_title ||
                      currentCourse.onlineCourseCategory ||
                      "-"}
                  </div>
                </div>
              </div>
              <div className="col-md-6 col-lg-4">
                <div className="p-3 bg-light rounded border h-100">
                  <small className="text-muted d-block uppercase fw-medium mb-1">Course Department</small>
                  <div className="fw-bold text-dark fs-15">
                    {(currentCourse.course_category as any)?.course_departments?.department ||
                      currentCourse.onlineCourseDepartment ||
                      "-"}
                  </div>
                </div>
              </div>
              <div className="col-md-6 col-lg-4">
                <div className="p-3 bg-light rounded border h-100">
                  <small className="text-muted d-block uppercase fw-medium mb-1">Online Course Name</small>
                  <div className="fw-bold text-primary fs-15">
                    {currentCourse.onlineCourseName || "-"}
                  </div>
                </div>
              </div>
              <div className="col-md-6 col-lg-4">
                <div className="p-3 bg-light rounded border h-100">
                  <small className="text-muted d-block uppercase fw-medium mb-1">Platform</small>
                  <div className="fw-bold text-dark fs-15">
                    {(currentCourse.course_category as any)?.course_departments?.plateform ||
                      (currentCourse.course_category as any)?.course_departments?.platform ||
                      "-"}
                  </div>
                </div>
              </div>
              <div className="col-md-6 col-lg-4">
                <div className="p-3 bg-light rounded border h-100">
                  <small className="text-muted d-block uppercase fw-medium mb-1">Duration of Course</small>
                  <div className="fw-bold text-dark fs-15">
                    {(currentCourse.course_category as any)?.course_departments?.duration ||
                      currentCourse.durationOfOnlineCourse ||
                      "-"}
                  </div>
                </div>
              </div>
              <div className="col-md-6 col-lg-4">
                <div className="p-3 bg-light rounded border h-100">
                  <small className="text-muted d-block uppercase fw-medium mb-1">Online Course Fee</small>
                  <div className="fw-bold text-dark fs-15">
                    {(currentCourse.course_category as any)?.course_departments?.course_fee
                      ? `₹ ${Number(
                        (currentCourse.course_category as any).course_departments.course_fee
                      ).toLocaleString("en-IN")}`
                      : "-"}
                  </div>
                </div>
              </div>
              <div className="col-md-12">
                <div className="p-3 bg-light rounded border h-100">
                  <small className="text-muted d-block uppercase fw-medium mb-1">Contributors</small>
                  <div className="fw-bold text-dark fs-15">
                    {(currentCourse.course_category as any)?.course_departments?.contributor || "-"}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 5. Previous Reimbursements for Online Courses */}
        <div className="card mt-4">
          <div className="card-header">
            <h5 className="mb-0">
              <i className="ti ti-history me-2 text-primary"></i>
              Previous Reimbursements for Online Courses
            </h5>
          </div>
          <div className="card-body">
            <div className="table-responsive">
              <table className="table table-hover table-bordered mb-0 align-middle">
                <thead className="table-light">
                  <tr>
                    <th>Category</th>
                    <th>Platform</th>
                    <th>Course Duration</th>
                    <th>Course Name</th>
                    <th>Date of Joining Course</th>
                    <th>Completion Date</th>
                    <th>Online Course Fee</th>
                    <th>Date of Reimbursement</th>
                    <th>End of Block</th>
                  </tr>
                </thead>
                <tbody>
                  {Array.isArray((currentCourse as any)?.previousReimbursements) &&
                    (currentCourse as any).previousReimbursements.length > 0 ? (
                    (currentCourse as any).previousReimbursements.map(
                      (item: any, idx: number) => (
                        <tr key={idx}>
                          <td>{item.category || item.category_title || "-"}</td>
                          <td>{item.platform || item.plateform || "-"}</td>
                          <td>{item.courseDuration || item.duration || "-"}</td>
                          <td>{item.courseName || item.onlineCourseName || "-"}</td>
                          <td>
                            {formatDate(
                              item.dateOfJoining || item.dateOfCourseJoining
                            )}
                          </td>
                          <td>
                            {formatDate(
                              item.completionDate || item.courseEndingDate
                            )}
                          </td>
                          <td>
                            {item.courseFee || item.onlineCourseFee
                              ? `₹ ${Number(
                                item.courseFee || item.onlineCourseFee
                              ).toLocaleString("en-IN")}`
                              : "-"}
                          </td>
                          <td>
                            {formatDate(
                              item.reimbursementDate || item.dateOfReimbursement
                            )}
                          </td>
                          <td>
                            {formatDate(item.endOfBlock) !== "-"
                              ? formatDate(item.endOfBlock)
                              : item.endOfBlock || "-"}
                          </td>
                        </tr>
                      )
                    )
                  ) : (
                    <tr>
                      <td
                        colSpan={9}
                        className="text-center py-3 text-muted bg-light"
                      >
                        <i className="ti ti-info-circle me-1"></i>
                        No reimbursement taken for Online course previously
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* 6. Instructions */}
        <div className="card mt-4">
          <div className="card-header">
            <h5 className="mb-0">
              <i className="ti ti-info-circle me-2 text-primary"></i>
              Instructions
            </h5>
          </div>
          <div className="card-body">
            <ul className="list-unstyled mb-0 d-flex flex-column gap-2 text-muted">
              <li className="d-flex align-items-start">
                <span className="text-primary me-2 fw-bold">•</span>
                <span>
                  For field 'Web URL', please provide link to programme brochure/website URL having details of fee, last date of registration etc.
                </span>
              </li>
              <li className="d-flex align-items-start">
                <span className="text-primary me-2 fw-bold">•</span>
                <span>
                  Updated course fee is to be filled if fee is currently different from the approved amount in the HR Compendium.
                </span>
              </li>
              <li className="d-flex align-items-start">
                <span className="text-primary me-2 fw-bold">•</span>
                <span>
                  If fee is in dollars, other currency, please provide amount in INR.
                </span>
              </li>
              <li className="d-flex align-items-start">
                <span className="text-primary me-2 fw-bold">•</span>
                <span>
                  If there is no fixed Last Registration Date then please enter today's date.
                </span>
              </li>
            </ul>
          </div>
        </div>

        {/* 7. Terms & Conditions */}
        <div className="card mt-4">
          <div className="card-header">
            <h5 className="mb-0">
              <i className="ti ti-list-check me-2 text-primary"></i>
              Terms & Conditions
            </h5>
          </div>
          <div className="card-body">
            <ol className="mb-0 ps-3 d-flex flex-column gap-2 text-muted">
              <li>
                Claim will be reimbursed after qualifying of the course and submission of copy of Certificate awarded, payment details.
              </li>
              <li>
                Provision for accessories, such as computer & peripherals, laptops, internet connections etc., would not be provided by DMRC and employee undergoing the courses, shall have to arrange the same.
              </li>
              <li>
                The online Courses Should not be pursued during office/duty hours.
              </li>
              <li>
                Any kind of special consideration (leave/transfer/shifting of duty etc.) to pursue the Online Course, shall not be entertained.
              </li>
              <li>
                No further incentive/s shall be permissible on successful completion of the Online Courses.
              </li>
              <li>
                The courses as contained in the approved list of courses shall only be pursued for reimbursement of course fee and addition qualification in service records. Request(s) for consideration of any other course will not be considered for the above purposes.
              </li>
              <li>
                Employees are advised to avail discount (if offered), by the University/College.
              </li>
              <li>
                An employee can pursue any number of courses; however, fee reimbursement shall be restricted to Rs. 18000/- per block of 3 years (from completion of one course).
              </li>
            </ol>
          </div>
        </div>

        {/* 8. Course Documents Section */}
        {currentCourse.documents && currentCourse.documents.length > 0 && (
          <div className="card mt-4">
            <div className="card-header">
              <h5 className="mb-0">
                <i className="ti ti-files me-2 text-primary"></i>
                Course Documents ({currentCourse.documents.length})
              </h5>
            </div>
            <div className="card-body">
              <div className="table-responsive">
                <table className="table table-hover">
                  <thead>
                    <tr>
                      <th scope="col">#</th>
                      <th scope="col">Document Type</th>
                      <th scope="col">Upload Date</th>
                      <th scope="col">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentCourse.documents.map((doc: any, index: number) => (
                      <tr key={index}>
                        <td>
                          <div className="d-flex align-items-center">
                            <i className="ti ti-file-text text-primary me-2"></i>
                            {index + 1}
                          </div>
                        </td>
                        <td>
                          <span className="badge bg-info-transparent text-info">
                            {doc.typeOfAttachedDoc || doc.documentType || "Course Document"}
                          </span>
                        </td>
                        <td>
                          <span className="text-muted">
                            {formatDate(doc.created_at)}
                          </span>
                        </td>
                        <td>
                          <button
                            type="button"
                            className="btn btn-sm btn-primary"
                            onClick={() => handleDocumentClick(doc.fileName || doc.awsKey)}
                            title="View/Download File"
                            disabled={!doc.fileName && !doc.awsKey}
                          >
                            <i className="ti ti-eye me-1"></i>
                            View
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* 9. Declaration */}
        <div className="card mt-4">
          <div className="card-header">
            <h5 className="mb-0">
              <i className="ti ti-checkbox me-2 text-primary"></i>
              Declaration
            </h5>
          </div>
          <div className="card-body">
            <p className="mb-0 text-muted fs-14">
              I hereby declare that the i will abide by all the rules and regulations of the Company. if  any disccrepancies found, I am liable to be dealt as per the Low.  .
            </p>
          </div>
        </div>



        {/* 11. Previous Remarks & Approval History Section */}
        {(() => {
          let rawList: any[] = [
            ...(Array.isArray((currentCourse as any)?.remarks_data) && (currentCourse as any).remarks_data.length > 0
              ? (currentCourse as any).remarks_data
              : Array.isArray((currentCourse as any)?.history) && (currentCourse as any).history.length > 0
                ? (currentCourse as any).history
                : Array.isArray((currentCourse as any)?.work_history) && (currentCourse as any).work_history.length > 0
                  ? (currentCourse as any).work_history
                  : Array.isArray((currentCourse as any)?.remarksHistory) && (currentCourse as any).remarksHistory.length > 0
                    ? (currentCourse as any).remarksHistory
                    : [])
          ].map((item: any) => ({ ...item }));

          const applicantId = String(
            (currentCourse as any)?.empId ||
            (currentCourse as any)?.empID ||
            (currentCourse as any)?.employee?.empId ||
            (currentCourse as any)?.employee?.empID ||
            ""
          ).replace(/^0+/, "");

          const hasEmployeeEntry = rawList.some((item: any) => {
            const itemUserId = String(
              item?.approverUserID || item?.userId || item?.empID || ""
            ).replace(/^0+/, "");
            return (
              item?.usertype === "E" ||
              item?.usertype === "Employee" ||
              item?.approverName === "User" ||
              (applicantId && itemUserId && itemUserId === applicantId)
            );
          });

          const employeeRemarks = (() => {
            const candidates = [
              (currentCourse as any)?.anyRemarks,
              (currentCourse as any)?.any_remarks,
              (currentCourse as any)?.employeeRemarks,
              (currentCourse as any)?.empRemarks,
              (currentCourse as any)?.remarks !== "X" ? (currentCourse as any)?.remarks : null,
              (currentCourse as any)?.Remarks !== "X" ? (currentCourse as any)?.Remarks : null,
            ];
            for (const cand of candidates) {
              if (typeof cand === "string" && cand.trim() && cand.trim() !== "X" && cand.trim() !== "-" && cand.trim() !== "null") {
                return cand.trim();
              }
            }
            return "";
          })();

          const employeeTimestamp =
            (currentCourse as any)?.requestCreationDate ||
            (currentCourse as any)?.created_at ||
            (currentCourse as any)?.createdAt ||
            (currentCourse as any)?.sap_created_at ||
            (currentCourse as any)?.sapCreatedAt ||
            (currentCourse as any)?.appliedDate ||
            null;

          const employeeName = (() => {
            if ((currentCourse as any)?.employee) {
              const fname = (currentCourse as any).employee.firstname || (currentCourse as any).employee.firstName || "";
              const lname = (currentCourse as any).employee.lsastname || (currentCourse as any).employee.lastname || (currentCourse as any).employee.lastName || "";
              const full = `${fname} ${lname}`.trim();
              if (full) return full;
            }
            return (currentCourse as any)?.employeeName || (currentCourse as any)?.empName || "Employee";
          })();

          const employeeUserId = (currentCourse as any)?.empId || (currentCourse as any)?.empID || (currentCourse as any)?.employee?.empId || "";

          if (hasEmployeeEntry) {
            rawList = rawList.map((item: any) => {
              const itemUserId = String(item?.approverUserID || item?.userId || item?.empID || "").replace(/^0+/, "");
              const isEmp = item?.usertype === "E" || item?.usertype === "Employee" || item?.approverName === "User" || (applicantId && itemUserId && itemUserId === applicantId);
              if (isEmp && !item?.remarks && employeeRemarks) {
                return { ...item, remarks: employeeRemarks };
              }
              return { ...item };
            });
          } else if (currentCourse && (employeeUserId || employeeName !== "Employee" || (currentCourse as any)?.id || (currentCourse as any)?.referenceNumber)) {
            rawList.push({
              usertype: "Employee",
              approverUserID: employeeUserId,
              userId: employeeUserId,
              empID: employeeUserId,
              approverName: employeeName,
              name: employeeName,
              remarks: employeeRemarks,
              createdAt: employeeTimestamp,
              isEmployee: true,
            });
          }

          if (rawList.length === 0) return null;

          return (
            <div className="card mt-4">
              <div className="card-header bg-light">
                <h5 className="card-title mb-0">
                  <i className="ti ti-history me-2 text-primary"></i>
                  Previous Remarks & Approval History
                </h5>
              </div>
              <div className="card-body">
                <div className="d-flex flex-column gap-3">
                  {rawList.map((item: any, index: number) => {
                    const itemUserId = String(item?.approverUserID || item?.userId || item?.empID || "").replace(/^0+/, "");
                    const isEmp = item?.usertype === "E" || item?.usertype === "Employee" || item?.approverName === "User" || (applicantId && itemUserId && itemUserId === applicantId);
                    return (
                      <div
                        key={index}
                        className={`p-3 bg-white rounded border border-start-4 ${isEmp ? "border-start-info" : "border-start-primary"} shadow-xs`}
                      >
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <div className="d-flex align-items-center gap-2">
                            <div className={`avatar avatar-sm ${isEmp ? "bg-info-transparent text-info" : "bg-primary-transparent text-primary"} rounded-circle fw-bold`}>
                              <i className={`${isEmp ? "ti ti-user" : "ti ti-user-check"} fs-16`}></i>
                            </div>
                            <div>
                              <div className="d-flex align-items-center gap-2">
                                <h6 className="mb-0 text-dark fw-bold">
                                  {item.approverName ||
                                    item.name ||
                                    item.history_approver?.name ||
                                    (isEmp ? "Employee" : "Approver")}
                                </h6>
                                <span className={`badge ${isEmp ? "bg-info-subtle text-info" : "bg-warning-subtle text-warning"} border text-xs`}>
                                  {isEmp ? "Employee" : "Approver"}
                                </span>
                              </div>
                              <small className="text-muted">
                                ID:{" "}
                                {formatEmpIdForDisplay(
                                  item.approverUserID ||
                                  item.userId ||
                                  item.history_approver?.empId ||
                                  (isEmp ? employeeUserId : "")
                                ) || "-"}
                              </small>
                            </div>
                          </div>
                          <span className="badge bg-light text-muted border">
                            <i className="ti ti-calendar me-1"></i>
                            {formatDate(
                              item.createdAt ||
                              item.created_at ||
                              item.timeStamp
                            )}
                            {(item.createdAt || item.created_at || item.timeStamp) && !isNaN(new Date(item.createdAt || item.created_at || item.timeStamp).getTime())
                              ? " " + new Date(item.createdAt || item.created_at || item.timeStamp).toLocaleTimeString([], {
                                hour: "2-digit",
                                minute: "2-digit",
                                second: "2-digit",
                                hour12: true,
                              }).toUpperCase()
                              : ""}
                          </span>
                        </div>
                        <div className="mt-2 pt-2 border-top">
                          <p className="mb-0 text-dark fw-medium fs-14">
                            {item.remarks ? (
                              <span>
                                <i className="ti ti-quote text-muted me-1"></i>
                                {item.remarks}
                              </span>
                            ) : (
                              <span className="text-muted fst-italic">No remarks</span>
                            )}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          );
        })()}

        {/* Read-only Notice banner */}
        <div className="alert alert-success-light d-flex align-items-center mt-4 bg-success-transparent text-success border border-success-subtle p-3 rounded">
          <i className="ti ti-info-circle me-2 fs-18"></i>
          <span>This is a read-only view of your submitted Online Course NOC application.</span>
        </div>
      </div>
    </div>
  );
};

export default OnlineCourseDetails;
